import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, CircleAlert, Download, FileText, Share2, Upload, X } from 'lucide-react';
import {
  loadLaboratorySharedData, loadLaboratorySharedFiles, matchLaboratorySharedFiles, openLaboratorySharedFile, removeLaboratorySharedFile,
  sendLaboratorySharedFile, startLaboratoryShare, tellLaboratoryShare,
} from './api';
import { dataSize } from './dataSize';
import { labError, labWhen } from './LaboratoryParts';
import { Segmented, XomicsSelect, useLeave } from './parts';
import { tx } from './i18n';
import './laboratory-shared-data.css';

// What the laboratory shares with a customer, from the page of their submission: sequencing files and
// anything else they asked for, on the whole project or on one sample, with a note. It has nothing to do
// with the Uploads page: nothing here starts an analysis. With hundreds of samples the laboratory drops
// everything at once and each file goes to the sample its name says, by their name or our code; a file
// with no clear match waits for the laboratory to choose. The customer sees a file once it is whole.
const text = (key, values) => tx(`ui.lab.customers.${key}`, values);
const counted = (key, count) => text(count === 1 ? `${key}_one` : key, { count });
const FILES_AT_ONCE = 3;
const SHOWN_ROWS = 100;

// How much is shared on the project, and the dialog that is open over the page.
export function useSharedData(projectUuid) {
  const [data, setData] = useState(null), [dialog, setDialog] = useState(null);
  const load = useCallback(() => loadLaboratorySharedData(projectUuid).then(setData).catch(() => setData(null)), [projectUuid]);
  useEffect(() => { load(); }, [load]);
  const counts = useMemo(() => new Map((data?.bySample ?? []).map(row => [row.position, row.files])), [data]);
  return { data, counts, dialog, setDialog, load };
}

// A sample's cell in the table of samples: how many files it has, and sharing on it alone.
export function SharedDataCell({ sample, mayWork, shared }) {
  const count = shared.counts.get(sample.position) ?? 0;
  return <span className="xo-labdata-cell">
    {count > 0
      ? <button type="button" className="xo-labdata-count" onClick={() => shared.setDialog({ kind: 'files', sample })}>{counted('data_files', count)}</button>
      : <small>{text('data_none')}</small>}
    {mayWork && shared.data?.available && <button type="button" className="xo-btn-2 xo-labdata-one" onClick={() => shared.setDialog({ kind: 'share', sample })}>
      {text('data_share_one')}</button>}
  </span>;
}

export function SharedDataCard({ box, mayWork, shared }) {
  const { data } = shared;
  const totals = data?.totals ?? null;
  return <section className="xo-labcust-card xo-labdata">
    <header><span className="xo-labcust-card-icon" aria-hidden="true"><Share2 /></span>
      <div><h3>{text('data_title')}</h3><p>{text('data_line')}</p></div></header>
    {totals && <ul className="xo-labdata-totals">
      <li>
        {totals.projectFiles > 0
          ? <button type="button" className="xo-labdata-count" onClick={() => shared.setDialog({ kind: 'files', sample: null })}>{text('data_whole_project')}</button>
          : <span>{text('data_whole_project')}</span>}
        <small>{counted('data_files', totals.projectFiles)}</small>
      </li>
      <li><span>{text('data_samples')}</span><small>{text('data_samples_have', { shared: totals.samplesWithFiles, total: totals.samples })}</small></li>
    </ul>}
    {data && !data.available && <p className="xo-labcust-none">{text('data_unavailable')}</p>}
    {mayWork && data?.available && <div className="xo-labcust-card-actions">
      <button type="button" className="xo-btn" onClick={() => shared.setDialog({ kind: 'share', sample: null })}><Upload aria-hidden="true" />{text('data_share')}</button>
    </div>}
    {shared.dialog?.kind === 'share' && <ShareDialog box={box} sample={shared.dialog.sample} onClose={() => shared.setDialog(null)} onDone={shared.load} />}
    {shared.dialog?.kind === 'files' && <FilesDialog box={box} sample={shared.dialog.sample} mayWork={mayWork}
      onClose={() => shared.setDialog(null)} onChanged={shared.load} />}
  </section>;
}

function Dialog({ title, subtitle, onClose, foot, children }) {
  return <div className="xo-dialog-layer" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="xo-dialog xo-labdata-dialog" role="dialog" aria-modal="true" aria-label={title}>
      <header className="xo-dialog-head">
        <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
        <button type="button" className="xo-dialog-close" onClick={onClose} aria-label={tx('ui.parts.close')}><X aria-hidden="true" /></button>
      </header>
      <div className="xo-dialog-body">{children}</div>
      {foot && <footer className="xo-dialog-foot">{foot}</footer>}
    </div>
  </div>;
}

// Where a file is going: chosen (a sample's place, or 'project'), or still for the laboratory to choose.
function Target({ entry, samples, busy, onChoose }) {
  if (entry.match === 'matched') return <span className="xo-labdata-chip">{entry.label}</span>;
  return <span className="xo-labdata-pick">
    {entry.target === undefined && <span className="xo-labdata-chip is-open">{text(entry.match === 'several' ? 'data_match_several' : 'data_match_none')}</span>}
    <XomicsSelect value={entry.target === undefined ? '' : String(entry.target)} disabled={busy} aria-label={text('data_choose_target', { name: entry.file.name })}
      onChange={event => onChoose(event.target.value)}>
      <option value="">{text('data_choose')}</option>
      <option value="project">{text('data_whole_project')}</option>
      {samples.map(sample => <option key={sample.position} value={String(sample.position)}>{sample.sampleName} · {sample.code}</option>)}
    </XomicsSelect>
  </span>;
}

function ShareDialog({ box, sample, onClose, onDone }) {
  const projectUuid = box.project.uuid;
  const [mode, setMode] = useState(sample ? 'sample' : 'project'), [position, setPosition] = useState(sample ? String(sample.position) : '');
  const [note, setNote] = useState(''), [tell, setTell] = useState(true), [entries, setEntries] = useState([]);
  const [phase, setPhase] = useState('choose'), [error, setError] = useState(''), [matching, setMatching] = useState(false);
  const [dragging, setDragging] = useState(false), [told, setTold] = useState(null);
  const input = useRef(null), batch = useRef(null), progress = useRef(new Map());
  const [, setTick] = useState(0);
  const sending = phase === 'sending';
  const leave = useLeave(sending, phase === 'choose' && (entries.length > 0 || note !== ''), onClose);

  // A page closed while files go up would stop them.
  useEffect(() => {
    if (!sending) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    const timer = window.setInterval(() => setTick(tick => tick + 1), 400);
    return () => { window.removeEventListener('beforeunload', warn); window.clearInterval(timer); };
  }, [sending]);

  const applyMatches = useCallback(async (list) => {
    if (list.length === 0) return;
    setMatching(true); setError('');
    try {
      const { matches } = await matchLaboratorySharedFiles(projectUuid, list.map(entry => entry.file.name));
      setEntries(list.map((entry, index) => {
        const found = matches[index];
        return found.state === 'matched'
          ? { ...entry, match: 'matched', target: found.sample.position, label: found.sample.sampleName }
          : { ...entry, match: found.state, target: undefined, label: null };
      }));
    } catch (caught) { setError(labError(caught)); } finally { setMatching(false); }
  }, [projectUuid]);

  function add(list) {
    const known = new Set(entries.map(entry => entry.key));
    const fresh = [...list].map(file => ({ key: `${file.name}/${file.size}/${file.lastModified}`, file, match: null, target: undefined, label: null, state: 'waiting', problem: null }))
      .filter(entry => !known.has(entry.key) && entry.file.size > 0);
    const all = [...entries, ...fresh];
    setEntries(all);
    if (mode === 'match') applyMatches(all);
  }
  function chooseMode(next) {
    setMode(next);
    if (next === 'match') applyMatches(entries);
  }
  const choose = (key, value) => setEntries(all => all.map(entry => (entry.key !== key ? entry
    : { ...entry, target: value === '' ? undefined : value === 'project' ? 'project' : Number(value) })));
  const drop = (key) => setEntries(all => all.filter(entry => entry.key !== key));

  const targetOf = (entry) => {
    if (mode === 'project') return null;
    if (mode === 'sample') return Number(position);
    return entry.target === 'project' ? null : entry.target;
  };
  const waiting = mode === 'match' ? entries.filter(entry => entry.target === undefined).length : 0;
  const ready = entries.length > 0 && !matching && waiting === 0 && (mode !== 'sample' || position !== '');
  const matched = entries.filter(entry => entry.match === 'matched').length;
  const totalBytes = entries.reduce((sum, entry) => sum + entry.file.size, 0);
  const doneBytes = entries.reduce((sum, entry) => sum + (entry.file.size * (entry.state === 'done' ? 1 : progress.current.get(entry.key) ?? 0)), 0);
  const percent = totalBytes > 0 ? Math.round((doneBytes / totalBytes) * 100) : 0;
  const failed = entries.filter(entry => entry.state === 'failed').length;
  const sentCount = entries.filter(entry => entry.state === 'done').length;

  async function share() {
    setPhase('sending'); setError('');
    try {
      if (batch.current === null) batch.current = (await startLaboratoryShare(projectUuid, note.trim())).uuid;
      const queue = entries.filter(entry => entry.state !== 'done');
      const results = new Map();
      const worker = async () => {
        for (;;) {
          const entry = queue.shift();
          if (!entry) return;
          setEntries(all => all.map(one => (one.key === entry.key ? { ...one, state: 'sending', problem: null } : one)));
          try {
            await sendLaboratorySharedFile(projectUuid, batch.current, entry.file, targetOf(entry), (share_) => progress.current.set(entry.key, share_));
            results.set(entry.key, null);
            setEntries(all => all.map(one => (one.key === entry.key ? { ...one, state: 'done' } : one)));
          } catch (caught) {
            results.set(entry.key, labError(caught));
            setEntries(all => all.map(one => (one.key === entry.key ? { ...one, state: 'failed', problem: labError(caught) } : one)));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(FILES_AT_ONCE, queue.length) }, worker));
      onDone();
      if ([...results.values()].some(Boolean)) { setPhase('retry'); return; }
      if (tell) setTold(await tellLaboratoryShare(projectUuid, batch.current));
      setPhase('done');
    } catch (caught) {
      setError(labError(caught));
      setPhase(batch.current === null ? 'choose' : 'retry');
    }
  }

  const rows = [...entries].sort((a, b) => Number(b.target === undefined && mode === 'match') - Number(a.target === undefined && mode === 'match')).slice(0, SHOWN_ROWS);
  const foot = phase === 'done'
    ? <button type="button" className="xo-btn" onClick={onClose}>{text('data_done')}</button>
    : <>
      <button type="button" className="xo-btn-2" disabled={sending} onClick={leave}>{text('cancel')}</button>
      <button type="button" className="xo-btn" disabled={!ready || sending} onClick={share}><Upload aria-hidden="true" />
        {sending ? text('data_sending', { percent }) : text(phase === 'retry' ? 'data_retry' : 'data_send')}</button>
    </>;

  return <Dialog title={text('data_share_with', { name: box.project.customer.name })} subtitle={text('data_share_detail', { number: box.project.number })} onClose={leave} foot={foot}>
    {phase === 'done' ? <p className="xo-labdata-result" role="status"><Check aria-hidden="true" />
      {counted('data_shared_done', entries.length)}{told ? ` ${counted('data_told', told.emailed.length)}` : ''}</p> : <>
      <div className="xo-labdata-field"><label>{text('data_for')}</label>
        <Segmented value={mode} onChange={chooseMode} options={[
          { value: 'project', label: text('data_for_project') }, { value: 'sample', label: text('data_for_sample') }, { value: 'match', label: text('data_for_match') },
        ]} /></div>
      {mode === 'sample' && <div className="xo-labdata-field"><label>{text('data_which_sample')}</label>
        <XomicsSelect value={position} disabled={sending} aria-label={text('data_which_sample')} onChange={event => setPosition(event.target.value)}>
          <option value="">{text('data_choose')}</option>
          {box.samples.map(one => <option key={one.position} value={String(one.position)}>{one.sampleName} · {one.code}</option>)}
        </XomicsSelect></div>}
      {mode === 'match' && <p className="xo-labdata-hint">{text('data_match_hint')}</p>}
      <div className="xo-labdata-field"><label htmlFor="xo-labdata-note">{text('data_note')}</label>
        <textarea id="xo-labdata-note" rows={2} maxLength={2000} value={note} disabled={sending || batch.current !== null} placeholder={text('data_note_placeholder')}
          onChange={event => setNote(event.target.value)} /></div>
      <div className="xo-labdata-field"><label>{text('data_files_title')}</label>
        <input ref={input} type="file" multiple hidden onChange={event => { add(event.target.files || []); event.target.value = ''; }} />
        <button type="button" className={`xo-labdata-drop${dragging ? ' is-over' : ''}`} disabled={sending} onClick={() => input.current?.click()}
          onDragOver={event => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
          onDrop={event => { event.preventDefault(); setDragging(false); add(event.dataTransfer.files); }}>
          <Upload aria-hidden="true" />{text('data_drop')}</button>
        {entries.length > 0 && <p className="xo-labdata-sum">
          <strong>{counted('data_files', entries.length)}</strong> · {dataSize(totalBytes)}
          {mode === 'match' && <> · {text('data_matched', { count: matched })}{waiting > 0 && <> · <b>{text('data_waiting', { count: waiting })}</b></>}</>}
          {phase !== 'choose' && <> · {text('data_sent', { count: sentCount })}</>}
        </p>}
        {rows.length > 0 && <ul className="xo-labdata-list">{rows.map(entry => <li key={entry.key}>
          <FileText aria-hidden="true" />
          <span className="xo-labdata-name">{entry.file.name}{entry.problem && <small className="is-bad">{entry.problem}</small>}</span>
          {mode === 'match' && <Target entry={entry} samples={box.samples} busy={sending} onChoose={value => choose(entry.key, value)} />}
          <small>{dataSize(entry.file.size)}</small>
          {phase === 'choose' ? <button type="button" className="xo-icon-btn" aria-label={text('data_remove_named', { name: entry.file.name })} onClick={() => drop(entry.key)}><X aria-hidden="true" /></button>
            : entry.state === 'done' ? <Check className="is-good" aria-hidden="true" />
              : entry.state === 'failed' ? <CircleAlert className="is-bad" aria-hidden="true" />
                : <span className="xo-labdata-bar"><i style={{ width: `${Math.round((progress.current.get(entry.key) ?? 0) * 100)}%` }} /></span>}
        </li>)}</ul>}
        {entries.length > SHOWN_ROWS && <p className="xo-labdata-sum">{text('data_more_rows', { count: entries.length - SHOWN_ROWS })}</p>}
      </div>
      <label className="xo-labdata-tell"><input type="checkbox" checked={tell} disabled={sending} onChange={event => setTell(event.target.checked)} />{text('data_tell')}</label>
      {failed > 0 && <p className="xo-alert t-bad" role="alert">{counted('data_failed', failed)}</p>}
      {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    </>}
  </Dialog>;
}

// What is shared on the whole project or on one sample, each time with its note. A file is taken back
// here: it leaves the customer's view at once.
function FilesDialog({ box, sample, mayWork, onClose, onChanged }) {
  const projectUuid = box.project.uuid;
  const [data, setData] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const load = useCallback(() => loadLaboratorySharedFiles(projectUuid, sample?.position ?? null).then(setData).catch(caught => setError(labError(caught))),
    [projectUuid, sample]);
  useEffect(() => { load(); }, [load]);
  useLeave(false, false, onClose);
  async function remove(file) {
    if (!window.confirm(text('data_remove_confirm', { name: file.name }))) return;
    setBusy(true); setError('');
    try { await removeLaboratorySharedFile(file.uuid); await load(); onChanged(); }
    catch (caught) { setError(labError(caught)); } finally { setBusy(false); }
  }
  return <Dialog title={sample ? sample.sampleName : text('data_whole_project')} subtitle={text('data_title')} onClose={onClose}>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {data?.batches.length === 0 && <p className="xo-labcust-none">{text('data_none')}</p>}
    {data?.batches.map(batch => <div key={batch.uuid} className="xo-labdata-batch">
      <small>{labWhen(batch.sharedAt)}{batch.sharedBy ? ` · ${batch.sharedBy}` : ''}</small>
      {batch.note && <p className="xo-labdata-note">{batch.note}</p>}
      <ul className="xo-labdata-list">{batch.files.map(file => <li key={file.uuid}>
        <FileText aria-hidden="true" /><span className="xo-labdata-name">{file.name}</span><small>{dataSize(file.bytes)}</small>
        <button type="button" className="xo-icon-btn" aria-label={text('data_open_named', { name: file.name })}
          onClick={() => openLaboratorySharedFile(file.uuid).catch(caught => setError(labError(caught)))}><Download aria-hidden="true" /></button>
        {mayWork && <button type="button" className="xo-icon-btn" disabled={busy} aria-label={text('data_take_back_named', { name: file.name })} onClick={() => remove(file)}><X aria-hidden="true" /></button>}
      </li>)}</ul>
    </div>)}
  </Dialog>;
}
